import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Card,
  CardHeader,
  mergeClasses,
  Tooltip,
  Subtitle1,
} from '@fluentui/react-components';
import { DismissRegular, Info16Regular } from '@fluentui/react-icons';
import { Mode, LayoutContext } from '@bingads-webui-react/app-layout-container';
import { IAIMessage } from '@bingads-webui/ads-copilot-ui';
import { I18nContext } from '@bingads-webui/copilot-i18n';
import { type ILogger } from '@bingads-webui-react/msa-copilot-common';

import { usePanelStyles } from './styles';
import { Fluctuations } from './fluctuations';
import { IDateRange } from './types';
import { useFluctuationsMessage } from '../../hooks/use-fluctuations-message';

export type InsightPanelProps = {
  replyToId: string,
  TimeZone: number,
  dateRange: IDateRange,
  dateFormat: string,
  logger?: ILogger,
  onFeedbackButtonClick: IAIMessage['onFeedbackButtonClick'];
};

export const InsightsPanel: React.FC<InsightPanelProps> = React.memo((props) => {
  const {
    replyToId,
    TimeZone,
    dateRange,
    dateFormat,
    logger,
    onFeedbackButtonClick,
  } = props;

  const i18n = useContext(I18nContext);
  const { change: changeLayout } = useContext(LayoutContext) as any;
  const [, setHasData] = useState(false);

  const { fluctuationSummaryMessage, fluctuationResult } = useFluctuationsMessage(replyToId);

  const headerText = useMemo(() => {
    if (fluctuationResult && fluctuationSummaryMessage) {
      const isSingleCampaign = fluctuationResult.Name === 'CampaignFluctuation';
      const campaignName = fluctuationResult?.Parameters?.CampaignName;

      return i18n?.getString(
        isSingleCampaign ? _TL_('Fluctuations - {{campaignName}}') : _TL_('Fluctuations - All Campaigns'),
        { campaignName },
      );
    }

    return i18n?.getString(_TL_('Fluctuations'));
  }, [fluctuationSummaryMessage, fluctuationResult, i18n]);

  const panelClasses = usePanelStyles();
  const closePanel = useCallback(() => {
    changeLayout({ overlay: { mode: Mode.Hidden, content: null } });
  }, [changeLayout]);

  useEffect(() => {
    setHasData((prevHasData) => {
      if (prevHasData && fluctuationSummaryMessage == null) {
        // data was previously available but it has been removed, close the panel
        setTimeout(() => closePanel(), 0);
      }
      return fluctuationSummaryMessage != null;
    });
  }, [fluctuationSummaryMessage]);

  return (
    <Card className={mergeClasses('insight-panel', panelClasses.root)} size="large">
      <CardHeader
        className={panelClasses.header}
        header={(
          <Subtitle1 className={mergeClasses('insight-panel-header-text', panelClasses.headerText)}>
            {headerText}
            <Tooltip content={i18n!.getString(_TL_('Fluctuations are detected from the last 90 days of your selected date range.'))} relationship="description">
              <Button size="small" appearance="transparent" icon={<Info16Regular />} />
            </Tooltip>
          </Subtitle1>
        )}
        action={(
          <Button
            className={mergeClasses('close-button')}
            appearance="transparent"
            icon={<DismissRegular />}
            aria-label={i18n?.getString(_TL_('Close'))}
            onClick={closePanel}
          />
        )}
      />
      <Fluctuations
        replyToId={replyToId}
        i18n={i18n!}
        TimeZone={TimeZone}
        dateFormat={dateFormat}
        dateRange={dateRange}
        logger={logger}
        onFeedbackButtonClick={onFeedbackButtonClick}
      />
    </Card>
  );
});
